import { useSnapshot } from 'valtio';

import TextNew from '@cartwhl/ui/base/TextNew';
import BookmarkIcon from '@cartwhl/ui/icons/BookmarkIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';

import { useStore, useStoreApi } from '../assetLibraryStore';
import { useAssetLibraryAssetActions } from '../hooks/useAssetLibraryAssetActions';
import AssetLibraryBlockedActionTooltip from './AssetLibraryTrashActionTooltip';

const NAV_BUTTON_CLASS =
  'border-primary text-primary hover:bg-secondary flex cursor-pointer items-center rounded-lg border px-4 transition-colors';

const DESTRUCTIVE_NAV_BUTTON_CLASS =
  'border-primary text-red hover:bg-secondary flex cursor-pointer items-center rounded-lg border px-4 transition-colors';

function getNavButtonClass(compact: boolean, destructive = false) {
  const sizeClass = compact ? 'h-[30px] gap-[3px] px-4' : 'h-10 gap-2 px-4';
  const baseClass = destructive
    ? DESTRUCTIVE_NAV_BUTTON_CLASS
    : NAV_BUTTON_CLASS;

  return `${baseClass} ${sizeClass}`;
}

export default function AssetLibraryViewNavButtons({
  compact = false,
}: {
  compact?: boolean;
}) {
  const store = useStore();
  const storeApi = useStoreApi();
  const { selectedAssetCount } = useSnapshot(storeApi.state);
  const { bookmarkAllSelected, requestMoveToTrash, canDeleteAllAssets } =
    useAssetLibraryAssetActions();
  const hasSelection = selectedAssetCount > 0;
  const selectedAssets = hasSelection ? store.getSelectedDisplayAssets() : [];
  const moveAllToTrashDisabled =
    hasSelection && !canDeleteAllAssets(selectedAssets);
  const navButtonClass = getNavButtonClass(compact);
  const destructiveNavButtonClass = getNavButtonClass(compact, true);
  const trashIconClass = compact ? 'size-4' : 'size-5';
  const bookmarkIconClass = compact ? 'size-5' : 'size-6';
  const textToken = compact ? '12/medium' : '14/medium';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className={navButtonClass}
        onClick={() => {
          if (hasSelection) {
            void bookmarkAllSelected();
            return;
          }

          store.setLibraryView('bookmarks');
        }}
      >
        <TextNew token={textToken}>
          {hasSelection ? 'Bookmark all' : 'My Bookmarks'}
        </TextNew>
        <BookmarkIcon className={`text-primary ${bookmarkIconClass}`} />
      </button>
      <AssetLibraryBlockedActionTooltip disabled={moveAllToTrashDisabled}>
        <button
          type="button"
          disabled={moveAllToTrashDisabled}
          className={`${hasSelection ? destructiveNavButtonClass : navButtonClass} ${
            moveAllToTrashDisabled ? 'cursor-not-allowed opacity-50' : ''
          }`}
          onClick={() => {
            if (moveAllToTrashDisabled) {
              return;
            }

            if (hasSelection) {
              requestMoveToTrash(store.getSelectedDisplayAssets());
              return;
            }

            store.setLibraryView('trash');
          }}
        >
          <TextNew token={textToken}>
            {hasSelection ? 'Move all to Trash' : 'View Trash'}
          </TextNew>
          <TrashIcon
            className={
              hasSelection ? trashIconClass : `text-primary ${trashIconClass}`
            }
          />
        </button>
      </AssetLibraryBlockedActionTooltip>
    </div>
  );
}
